import Phaser from 'phaser';
import { Preferences } from '../WebPreferences';
import audioManager from '../AudioManager';
import { showRewarded, showInterstitial, showRewardedCoin, showBanner, hideBanner, BANNER_HEIGHT } from '../AdManager';
import { submitScore } from '../LeaderboardManager';
import { getAdsRemoved } from '../IAPManager';
import { initRating, recordGameOver, shouldAskForRating, markAsked, markNeverAsk, openPlayStoreListing } from '../RatingManager';
import { getCoins, canWatchAdForCoins, recordAdWatch, getAdWatchesRemaining, COINS_PER_AD, MAX_ADS_PER_DAY } from '../CoinManager';

export default class UIScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UIScene' });
    this.highAnimals       = 0;
    this.highHeight        = 0;
    this.gameOverContainer = null;
    this.rotateBtn         = null;
    this.rotateBtnText     = null;
    this.rotateBtnHit      = null;
    this.animalText        = null;
    this.heightText        = null;
    this.bestAnimalsText   = null;
    this.bestHeightText    = null;
    this.pauseContainer    = null;
    this.gameMode          = 'solo';
    this.turnIndicator     = null;
    this.botScoreText      = null;
    this.rescueContainer   = null;
    this.qualifyingLosses  = 0;
    this.rotateAdContainer = null;
    this.previewAnimalContainer = null;
    this.nextAnimalRevealContainer = null;
    this.previewNextBtn    = null;
    this.ratingContainer   = null;
    this.coinCounterText   = null;
    this.coinContainer     = null;
  }

  create(data) {
    this.gameMode = (data && data.mode === 'vs_bot') ? 'vs_bot' : 'solo';
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;
    // Best scores are loaded below, after the text nodes are created.

    // ── Score layout constants ────────────────────────────────────
    const LEFT_X   = 72 * sd;
    const RIGHT_X  = W - 72 * sd;
    const LBL_SIZE = 12 * sd;
    const VAL_SIZE = 20 * sd;
    const STROKE   = 3 * dpr;

    // Row 1 — animals
    const ROW1_LBL = 58 * sd;
    const ROW1_VAL = 73 * sd;
    // Row 2 — height  (below row 1)
    const ROW2_LBL = 102 * sd;
    const ROW2_VAL = 117 * sd;

    const LIVE_ANIMAL_COLOR  = '#aaffaa';
    const LIVE_HEIGHT_COLOR  = '#ffff44';
    const BEST_COLOR         = '#ffcc55';

    const lbl = (x, y, txt, color) => this.add.text(x, y, txt, {
      fontSize: `${LBL_SIZE}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color, stroke: '#000000', strokeThickness: STROKE,
    }).setOrigin(0.5, 0).setDepth(10);

    const val = (x, y, txt, color, size = VAL_SIZE) => this.add.text(x, y, txt, {
      fontSize: `${size}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color, stroke: '#000000', strokeThickness: STROKE,
    }).setOrigin(0.5, 0).setDepth(10);

    if (this.gameMode === 'vs_bot') {
      // ── VS BOT HUD: You score (left) + Bot score (right), no height ──
      lbl(LEFT_X,  ROW1_LBL, 'You 🐾',   LIVE_ANIMAL_COLOR);
      this.animalText = val(LEFT_X, ROW1_VAL, '0', LIVE_ANIMAL_COLOR);

      lbl(RIGHT_X, ROW1_LBL, 'Bot 🤖',   '#ff9944');
      this.botScoreText = val(RIGHT_X, ROW1_VAL, '0', '#ff9944');

      this.heightText      = null;
      this.bestAnimalsText = null;
      this.bestHeightText  = null;
    } else {
      // ── Solo HUD: original layout ─────────────────────────────────
      lbl(LEFT_X, ROW1_LBL, 'Animals 🐾',  LIVE_ANIMAL_COLOR);
      this.animalText = val(LEFT_X, ROW1_VAL, '0', LIVE_ANIMAL_COLOR);

      lbl(LEFT_X, ROW2_LBL, 'Height 📏',   LIVE_HEIGHT_COLOR);
      this.heightText = val(LEFT_X, ROW2_VAL, '0', LIVE_HEIGHT_COLOR);

      lbl(RIGHT_X, ROW1_LBL, 'Best Animals 🏆', BEST_COLOR);
      this.bestAnimalsText = val(RIGHT_X, ROW1_VAL, `${this.highAnimals}`, BEST_COLOR);

      lbl(RIGHT_X, ROW2_LBL, 'Best Height 🏆',  BEST_COLOR);
      this.bestHeightText  = val(RIGHT_X, ROW2_VAL, `${this.highHeight}`,  BEST_COLOR);

      // Update best-score display once Preferences load
      Preferences.get({ key: 'highAnimals' }).then(r => {
        this.highAnimals = parseInt(r.value) || 0;
        if (this.bestAnimalsText) this.bestAnimalsText.setText(`${this.highAnimals}`);
      });
      Preferences.get({ key: 'highHeight' }).then(r => {
        this.highHeight = parseInt(r.value) || 0;
        if (this.bestHeightText) this.bestHeightText.setText(`${this.highHeight}`);
      });
    }

    // ── Coin counter (top center) ────────────────────────────────
    const coinY = 42 * sd;
    const coinIconSize = 18 * sd;

    const coinBg = this.add.graphics().setDepth(10);
    coinBg.fillStyle(0x000000, 0.4);
    coinBg.fillRoundedRect(W / 2 - 50 * sd, coinY - 14 * sd, 100 * sd, 28 * sd, 14 * sd);

    // Coin icon
    const coinIcon = this.add.graphics().setDepth(11);
    coinIcon.fillStyle(0xddaa00, 1);
    coinIcon.fillCircle(W / 2 - 30 * sd, coinY, coinIconSize * 0.5);
    coinIcon.fillStyle(0xffdd44, 1);
    coinIcon.fillCircle(W / 2 - 30 * sd, coinY, coinIconSize * 0.38);
    coinIcon.fillStyle(0xddaa00, 1);
    coinIcon.fillCircle(W / 2 - 30 * sd, coinY, coinIconSize * 0.15);

    this.coinCounterText = this.add.text(W / 2 + 5 * sd, coinY, '0', {
      fontSize: `${16 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(11);

    getCoins().then(coins => {
      if (this.coinCounterText) this.coinCounterText.setText(`${coins}`);
    });

    // ── Hint text ─────────────────────────────────────────────────
    this.add.text(W / 2, 68 * sd, 'Tap to drop  •  Drag to aim', {
      fontSize: `${11 * sd}px`,
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(10);

    // ── Turn indicator (VS BOT mode only) ─────────────────────────
    this.turnIndicator = this.add.text(W / 2, 88 * sd, 'YOUR TURN', {
      fontSize: `${14 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#00ffcc',
      stroke: '#000000',
      strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(10).setVisible(this.gameMode === 'vs_bot');

    // ── Rotate button — centred at bottom ─────────────────────────
    const bannerOffset = BANNER_HEIGHT * dpr;
    const btnW = Math.min(200 * sd, W * 0.5);  // ← button width
    const btnH = 65 * sd;
    const btnY = H - btnH / 2 - 20 * sd - bannerOffset;
    const btnRadius = 20 * sd;  // ← rounded corners

    const drawRotateBtn = (color) => {
      this.rotateBtn.clear();
      this.rotateBtn.fillStyle(color, 1);
      this.rotateBtn.fillRoundedRect(W / 2 - btnW / 2, btnY - btnH / 2, btnW, btnH, btnRadius);
    };

    this.rotateBtn = this.add.graphics().setDepth(10);
    drawRotateBtn(0x90dd5c);

    this.rotateBtnHit = this.add.rectangle(W / 2, btnY, btnW, btnH)
      .setInteractive({ useHandCursor: true })
      .setDepth(10);
    const rotateBtnHit = this.rotateBtnHit;

    this.rotateBtnText = this.add.text(W / 2, btnY, '🔄  ROTATE (12)', {
      fontSize: `${22 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 4 * dpr,
    }).setOrigin(0.5).setDepth(11);

    // Update button text if ads already removed
    getAdsRemoved().then(adsRemoved => {
      if (adsRemoved && this.rotateBtnText) {
        this.rotateBtnText.setText('🔄  ROTATE (∞)');
      }
    });

    rotateBtnHit.on('pointerover',  () => drawRotateBtn(0xaae87a));
    rotateBtnHit.on('pointerout',   () => drawRotateBtn(0x75bf44));
    rotateBtnHit.on('pointerdown',  () => {
      this.tweens.add({
        targets: [this.rotateBtnText],
        scaleX: 0.93, scaleY: 0.93, duration: 70, yoyo: true,
      });
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.rotatePreview();
    });
    // ── Pause button — bottom left ────────────────────────────────
    const pauseBtnSize = 55 * sd;
    const pauseBtnX = pauseBtnSize / 2 + 20 * sd;
    const pauseBtnY = btnY;

    const pauseBtn = this.add.graphics().setDepth(10);
    const drawPauseBtn = (color) => {
      pauseBtn.clear();
      pauseBtn.fillStyle(color, 1);
      pauseBtn.fillRoundedRect(pauseBtnX - pauseBtnSize / 2, pauseBtnY - pauseBtnSize / 2, pauseBtnSize, pauseBtnSize, 12 * sd);
    };
    drawPauseBtn(0xff6600);

    //const pauseHit = this.add.rectangle(pauseBtnX, pauseBtnY, pauseBtnSize, pauseBtnSize)
      //.setInteractive({ useHandCursor: true }).setAlpha(0).setDepth(10);
    pauseBtn.setInteractive(
      new Phaser.Geom.Rectangle(pauseBtnX - pauseBtnSize / 2, pauseBtnY - pauseBtnSize / 2, pauseBtnSize, pauseBtnSize),
      Phaser.Geom.Rectangle.Contains
    );  

    this.add.text(pauseBtnX, pauseBtnY, '⏸', {
      fontSize: `${26 * sd}px`,
      fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5).setDepth(11);

    pauseBtn.on('pointerdown',  () => {
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.pauseGame();
    });

    // ── Preview Next Animal button — bottom right ─────────────────
    const previewBtnSize = 55 * sd;
    const previewBtnX = W - previewBtnSize / 2 - 20 * sd;
    const previewBtnY = btnY;

    const previewBtn = this.add.graphics().setDepth(10);
    const drawPreviewBtn = (color) => {
      previewBtn.clear();
      previewBtn.fillStyle(color, 1);
      previewBtn.fillRoundedRect(previewBtnX - previewBtnSize / 2, previewBtnY - previewBtnSize / 2, previewBtnSize, previewBtnSize, 12 * sd);
    };
    drawPreviewBtn(0x6644cc);

    previewBtn.setInteractive(
      new Phaser.Geom.Rectangle(previewBtnX - previewBtnSize / 2, previewBtnY - previewBtnSize / 2, previewBtnSize, previewBtnSize),
      Phaser.Geom.Rectangle.Contains
    );

    this.add.text(previewBtnX, previewBtnY, '🦁', {
      fontSize: `${26 * sd}px`,
      fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5).setDepth(11);

    previewBtn.on('pointerover', () => drawPreviewBtn(0x8866ee));
    previewBtn.on('pointerout',  () => drawPreviewBtn(0x6644cc));
    previewBtn.on('pointerdown', async () => {
      drawPreviewBtn(0x442299);
      const adsRemoved = await getAdsRemoved();
      const gameScene = this.scene.get('GameScene');

      if (adsRemoved) {
        // Instant reveal without ad
        if (gameScene) {
          const nextAnimal = gameScene.getNextAnimalName();
          this.showNextAnimalReveal(nextAnimal, sd, dpr);
          gameScene.nextAnimalRevealed = true;
          gameScene.events.emit('nextAnimalStatusUpdate', true);
        }
      } else {
        // Show ad offer
        this.showPreviewNextAnimalOffer();
      }
    });

    this.previewNextBtn = previewBtn;

    // Add golden badge if ads removed
    getAdsRemoved().then(adsRemoved => {
      if (adsRemoved) {
        drawPreviewBtn(0xddaa00); // Gold color to indicate premium feature
      }
    });

    // ── GameScene event listeners ─────────────────────────────────
    const game = this.scene.get('GameScene');

    if (game) {
      game.events.off('scoreUpdate').on('scoreUpdate', score => {
        if (this.animalText) this.animalText.setText(`${score}`);
      });

      game.events.off('heightUpdate').on('heightUpdate', height => {
        if (this.heightText) this.heightText.setText(`${height}`);
      });

      game.events.off('playerScoreUpdate').on('playerScoreUpdate', score => {
        if (this.animalText) this.animalText.setText(`${score}`);
      });

      game.events.off('botScoreUpdate').on('botScoreUpdate', score => {
        if (this.botScoreText) this.botScoreText.setText(`${score}`);
      });

      game.events.off('gameOver').on('gameOver', async ({ animals, towerHeight, loser, playerScore, botScore }) => {
        if (this.gameMode === 'solo') {
          if (animals > this.highAnimals) {
            this.highAnimals = animals;
            Preferences.set({ key: 'highAnimals', value: String(animals) });
            if (this.bestAnimalsText) this.bestAnimalsText.setText(`${animals}`);
          }
          if (towerHeight > this.highHeight) {
            this.highHeight = towerHeight;
            Preferences.set({ key: 'highHeight', value: String(towerHeight) });
            if (this.bestHeightText) this.bestHeightText.setText(`${towerHeight}`);
          }
          submitScore(animals, towerHeight);
        }

        await recordGameOver();
        if (shouldAskForRating()) {
          await markAsked();
          this._pendingGameOver = { animals, towerHeight, loser, playerScore, botScore };
          this.showRatingBanner();
        } else {
          this.showGameOver(animals, towerHeight, loser, playerScore, botScore);
        }
      });

      game.events.off('turnChanged').on('turnChanged', (turn) => {
        if (!this.turnIndicator) return;
        if (turn === 'player') {
          this.turnIndicator.setText('YOUR TURN').setColor('#00ffcc');
        } else {
          this.turnIndicator.setText("BOT'S TURN").setColor('#ff9944');
        }
        if (this.gameMode === 'vs_bot') {
          const isPlayer = turn === 'player';
          if (this.rotateBtn)     this.rotateBtn.setVisible(isPlayer);
          if (this.rotateBtnText) this.rotateBtnText.setVisible(isPlayer);
          if (this.rotateBtnHit)  this.rotateBtnHit.setVisible(isPlayer);
        }
      });

      game.events.off('gameRestarted').on('gameRestarted', async () => {
        if (this.animalText)   this.animalText.setText('0');
        if (this.heightText)   this.heightText.setText('0');
        if (this.botScoreText) this.botScoreText.setText('0');
        if (this.gameOverContainer) {
          this.gameOverContainer.destroy(true);
          this.gameOverContainer = null;
        }
        if (this.rotateBtn)     this.rotateBtn.setVisible(true);
        if (this.rotateBtnText) {
          this.rotateBtnText.setVisible(true);
          const adsRemoved = await getAdsRemoved();
          this.rotateBtnText.setText(adsRemoved ? '🔄  ROTATE (∞)' : '🔄  ROTATE (12)');
        }
        if (this.rotateBtnHit)  this.rotateBtnHit.setVisible(true);
        if (this.previewNextBtn) this.previewNextBtn.setVisible(true);
        if (this.turnIndicator && this.gameMode === 'vs_bot') {
          this.turnIndicator.setText('YOUR TURN').setColor('#00ffcc').setVisible(true);
        }
      });
      
      game.events.off('gamePaused').on('gamePaused', () => {
        this.showPaused();
      });

      game.events.off('gameResumed').on('gameResumed', () => {
        if (this.pauseContainer) {
          this.pauseContainer.destroy(true);
          this.pauseContainer = null;
        }
      });

      game.events.off('offerRescue').on('offerRescue', () => {
        this.showRescueOffer();
      });

      game.events.off('rescueComplete').on('rescueComplete', ({ score }) => {
        if (this.rescueContainer) {
          this.rescueContainer.destroy(true);
          this.rescueContainer = null;
        }
        if (this.rotateBtn)     this.rotateBtn.setVisible(true);
        if (this.rotateBtnText) this.rotateBtnText.setVisible(true);
        if (this.rotateBtnHit)  this.rotateBtnHit.setVisible(true);
        if (this.animalText)    this.animalText.setText(`${score}`);
      });

      game.events.off('rotationCountUpdate').on('rotationCountUpdate', async (count) => {
        if (this.rotateBtnText) {
          const adsRemoved = await getAdsRemoved();
          if (adsRemoved) {
            this.rotateBtnText.setText('🔄  ROTATE (∞)');
          } else {
            this.rotateBtnText.setText(`🔄  ROTATE (${count})`);
          }
        }
      });

      game.events.off('rotationsExhausted').on('rotationsExhausted', () => {
        this.showRotateAdOffer();
      });

      game.events.off('nextAnimalStatusUpdate').on('nextAnimalStatusUpdate', (revealed) => {
        if (this.previewNextBtn) {
          this.previewNextBtn.setVisible(!revealed);
        }
      });

      game.events.off('coinEarned').on('coinEarned', (newBalance) => {
        if (this.coinCounterText) this.coinCounterText.setText(`${newBalance}`);
      });

    }

    showBanner();
    initRating();
  }

  /* ─────────────────────── RATING BANNER ────────────────────────── */
  showRatingBanner() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.rotateBtn)     this.rotateBtn.setVisible(false);
    if (this.rotateBtnText) this.rotateBtnText.setVisible(false);

    this.ratingContainer = this.add.container(0, 0).setDepth(30);

    const cardW = Math.min(320 * sd, W * 0.85);
    const gap   = 48 * sd;

    const titleY    = H / 2 - gap * 2.0;
    const subY      = titleY   + gap * 0.8;
    const starsY    = subY     + gap * 1.1;
    const feedbackY = starsY   + gap * 0.7;
    const laterY    = feedbackY + gap * 1.0;
    const neverY    = laterY   + gap * 0.65;

    const cardH       = neverY - titleY + gap;
    const cardCentreY = (titleY + neverY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
      .setInteractive();
    const card = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    const titleTxt = this.add.text(W / 2, titleY, '⭐  Enjoying Animal Stack?', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00',
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY, 'Tap a star to rate us!', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#cccccc', align: 'center',
    }).setOrigin(0.5);

    const feedbackTxt = this.add.text(W / 2, feedbackY, '', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaffaa', align: 'center',
    }).setOrigin(0.5);

    const starSize = 38 * sd;
    const starGap  = 10 * sd;
    const totalStarsW = 5 * starSize + 4 * starGap;
    const startX = W / 2 - totalStarsW / 2 + starSize / 2;
    let selectedRating = 0;

    const stars = [];
    for (let i = 0; i < 5; i++) {
      const sx = startX + i * (starSize + starGap);
      const star = this.add.text(sx, starsY, '☆', {
        fontSize: `${starSize}px`, fontFamily: 'Arial, sans-serif',
        color: '#888888',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      star.on('pointerover', () => {
        for (let j = 0; j <= i; j++) {
          stars[j].setText('★').setColor('#ffdd00');
        }
        for (let j = i + 1; j < 5; j++) {
          if (j >= selectedRating) stars[j].setText('☆').setColor('#888888');
        }
      });

      star.on('pointerout', () => {
        for (let j = 0; j < 5; j++) {
          if (j < selectedRating) {
            stars[j].setText('★').setColor('#ffdd00');
          } else {
            stars[j].setText('☆').setColor('#888888');
          }
        }
      });

      star.on('pointerdown', () => {
        selectedRating = i + 1;
        for (let j = 0; j < 5; j++) {
          if (j < selectedRating) {
            stars[j].setText('★').setColor('#ffdd00');
          } else {
            stars[j].setText('☆').setColor('#888888');
          }
        }

        if (selectedRating >= 4) {
          feedbackTxt.setText('Thanks! Taking you to the Play Store...').setColor('#aaffaa');
          markNeverAsk();
          this.time.delayedCall(800, () => {
            openPlayStoreListing();
            this._dismissRatingBanner();
          });
        } else {
          feedbackTxt.setText('Thanks for your feedback!').setColor('#ffaa44');
          this.time.delayedCall(1200, () => {
            this._dismissRatingBanner();
          });
        }
      });

      stars.push(star);
    }

    const laterTxt = this.add.text(W / 2, laterY, 'Maybe Later', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    laterTxt.on('pointerover', () => laterTxt.setColor('#dddddd'));
    laterTxt.on('pointerout',  () => laterTxt.setColor('#aaaaaa'));
    laterTxt.on('pointerdown', () => {
      this._dismissRatingBanner();
    });

    const neverTxt = this.add.text(W / 2, neverY, "Don't ask again", {
      fontSize: `${12 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#666666',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    neverTxt.on('pointerover', () => neverTxt.setColor('#999999'));
    neverTxt.on('pointerout',  () => neverTxt.setColor('#666666'));
    neverTxt.on('pointerdown', () => {
      markNeverAsk();
      this._dismissRatingBanner();
    });

    this.ratingContainer.add([
      overlay, card, titleTxt, subTxt,
      ...stars, feedbackTxt,
      laterTxt, neverTxt,
    ]);
  }

  _dismissRatingBanner() {
    if (this.ratingContainer) {
      this.ratingContainer.destroy(true);
      this.ratingContainer = null;
    }
    if (this._pendingGameOver) {
      const { animals, towerHeight, loser, playerScore, botScore } = this._pendingGameOver;
      this._pendingGameOver = null;
      this.showGameOver(animals, towerHeight, loser, playerScore, botScore);
    }
  }

  /* ─────────────────────── GAME OVER SCREEN ────────────────────── */
  showGameOver(animals, towerHeight, loser, playerScore, botScore) {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;
    const isBot = this.gameMode === 'vs_bot';

    if (this.rotateBtn)     this.rotateBtn.setVisible(false);
    if (this.rotateBtnText) this.rotateBtnText.setVisible(false);

    this.gameOverContainer = this.add.container(0, 0).setDepth(20);

    const cardW = Math.min(320 * sd, W * 0.85);

    // ── Spacing ───────────────────────────────────────────────────
    const gap      = 55 * sd;
    const titleY   = H / 2 - gap * (isBot ? 2.0 : 2.5);
    const lbl1Y    = titleY   + gap;
    const val1Y    = lbl1Y    + gap;
    const lbl2Y    = val1Y    + gap;
    const val2Y    = lbl2Y    + gap;
    const playBtnY = isBot ? val1Y + gap : val2Y + gap;
    const homeBtnY = playBtnY + gap;

    const cardH       = homeBtnY - titleY + gap * 1.2;
    const cardCentreY = (titleY + homeBtnY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.7);
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    // ── Title ─────────────────────────────────────────────────────
    let titleStr, titleColor;
    if (loser === 'player') {
      titleStr = '😢 You Lost!';
      titleColor = '#ff6666';
    } else if (loser === 'bot') {
      titleStr = '🎉 You Win!';
      titleColor = '#44ff88';
    } else {
      titleStr = '🐘 Tower Fell! 🦒';
      titleColor = '#ffcc00';
    }

    const title = this.add.text(W / 2, titleY, titleStr, {
      fontSize: `${24 * sd}px`, fontFamily: 'Arial, sans-serif', color: titleColor,
    }).setOrigin(0.5);

    const allItems = [overlay, card, title];

    if (isBot) {
      // ── VS BOT card: show You vs Bot scores, no highscores ────────
      const lbl1 = this.add.text(W / 2, lbl1Y, 'YOU  vs  BOT', {
        fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#aaaaaa',
      }).setOrigin(0.5);

      const youScore = this.add.text(W / 2 - cardW * 0.2, val1Y, `${playerScore ?? 0}`, {
        fontSize: `${42 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#aaffaa',
      }).setOrigin(0.5);

      const sep = this.add.text(W / 2, val1Y, '—', {
        fontSize: `${28 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#888888',
      }).setOrigin(0.5);

      const botScoreEl = this.add.text(W / 2 + cardW * 0.2, val1Y, `${botScore ?? 0}`, {
        fontSize: `${42 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#ff9944',
      }).setOrigin(0.5);

      allItems.push(lbl1, youScore, sep, botScoreEl);
    } else {
      // ── Solo card: original layout with highscores ────────────────
      const lbl1 = this.add.text(W / 2, lbl1Y, 'ANIMALS STACKED', {
        fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#aaaaaa',
      }).setOrigin(0.5);

      const val1 = this.add.text(W / 2 - cardW * 0.15, val1Y, `${animals}`, {
        fontSize: `${42 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#aaffaa',
      }).setOrigin(0.5);

      const best1 = this.add.text(W / 2 + cardW * 0.22, val1Y, `best\n${this.highAnimals}`, {
        fontSize: `${15 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#aaffaa', align: 'center',
      }).setOrigin(0.5);

      const lbl2 = this.add.text(W / 2, lbl2Y, 'TOWER HEIGHT', {
        fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#aaaaaa',
      }).setOrigin(0.5);

      const val2 = this.add.text(W / 2 - cardW * 0.15, val2Y, `${towerHeight}`, {
        fontSize: `${42 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#ffff44',
      }).setOrigin(0.5);

      const best2 = this.add.text(W / 2 + cardW * 0.22, val2Y, `best\n${this.highHeight}`, {
        fontSize: `${15 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#ffff88', align: 'center',
      }).setOrigin(0.5);

      allItems.push(lbl1, val1, best1, lbl2, val2, best2);
    }

    // ── Play Again button ─────────────────────────────────────────
    const btnW2 = Math.min(240 * sd, W * 0.6);
    const btnH2 = 60 * sd;
    const btn   = this.add.rectangle(W / 2, playBtnY, btnW2, btnH2, 0xffcc00)
      .setInteractive({ useHandCursor: true });
    const btnText = this.add.text(W / 2, playBtnY, '▶  Play Again', {
      fontSize: `${24 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#1a1a2e',
    }).setOrigin(0.5);

    btn.on('pointerover',  () => btn.setFillStyle(0xffe066));
    btn.on('pointerout',   () => btn.setFillStyle(0xffcc00));
    btn.on('pointerdown',  () => {
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.restartGame();
    });

    // ── Home button ───────────────────────────────────────────────
    const homeBtnW = Math.min(200 * sd, W * 0.5);
    const homeBtnH = 50 * sd;
    const homeBtn  = this.add.rectangle(W / 2, homeBtnY, homeBtnW, homeBtnH, 0x4488ff)
      .setInteractive({ useHandCursor: true });
    const homeBtnText = this.add.text(W / 2, homeBtnY, '🏠  Home', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    homeBtn.on('pointerover',  () => homeBtn.setFillStyle(0x66aaff));
    homeBtn.on('pointerout',   () => homeBtn.setFillStyle(0x4488ff));
    homeBtn.on('pointerdown', () => {
      // Track qualifying losses for interstitial threshold (solo only, ≥4 animals)
      if (this.gameMode === 'solo' && animals >= 4) {
        this.qualifyingLosses++;
      }

      const goHome = () => {
        this.scene.stop('GameScene');
        this.scene.start('HomeScene', {
          highAnimals: this.highAnimals,
          highHeight: this.highHeight
        });
        this.scene.stop('UIScene');
      };

      // Show interstitial simulation every 1st qualifying loss
      if (this.qualifyingLosses >= 1) {
        this.qualifyingLosses = 0;
        this.showInterstitialSim(goHome);
      } else {
        goHome();
      }
    });

    allItems.push(btn, btnText, homeBtn, homeBtnText);
    this.gameOverContainer.add(allItems);
  }
  /* ─────────────────────── RESCUE OFFER ───────────────────────── */
  showRescueOffer() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.rotateBtn)     this.rotateBtn.setVisible(false);
    if (this.rotateBtnText) this.rotateBtnText.setVisible(false);

    this.rescueContainer = this.add.container(0, 0).setDepth(25);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 52 * sd;

    const titleY  = H / 2 - gap * 1.8;
    const subY    = titleY + gap * 0.85;
    const timerY  = subY   + gap * 1.0;
    const saveBtnY = timerY + gap * 1.1;
    const giveUpY  = saveBtnY + gap * 0.85;

    const cardH       = giveUpY - titleY + gap;
    const cardCentreY = (titleY + giveUpY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.75);
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xff6644);

    const titleTxt = this.add.text(W / 2, titleY, '💔  Tower Fell!', {
      fontSize: `${24 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ff6644',
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY,
      'Watch a short ad to restore\nyour tower and keep going!', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#cccccc', align: 'center',
    }).setOrigin(0.5);

    // Countdown number
    const timerTxt = this.add.text(W / 2, timerY, '5', {
      fontSize: `${44 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00', stroke: '#000000', strokeThickness: 4 * dpr,
    }).setOrigin(0.5);

    // Save button
    const saveBtnW = Math.min(240 * sd, W * 0.6);
    const saveBtnH = 62 * sd;
    const saveBtn  = this.add.rectangle(W / 2, saveBtnY, saveBtnW, saveBtnH, 0x44cc66)
      .setInteractive({ useHandCursor: true });
    const saveBtnText = this.add.text(W / 2, saveBtnY, '📺  Save My Tower', {
      fontSize: `${21 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    // Give up text-button
    const giveUpTxt = this.add.text(W / 2, giveUpY, 'Give Up', {
      fontSize: `${14 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#888888',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.rescueContainer.add([
      overlay, card, titleTxt, subTxt, timerTxt,
      saveBtn, saveBtnText, giveUpTxt,
    ]);

    // ── Dismiss helper (called on give-up OR countdown end) ───────
    const dismiss = () => {
      if (countdown) countdown.remove();
      if (this.rescueContainer) {
        this.rescueContainer.destroy(true);
        this.rescueContainer = null;
      }
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.declineRescue();
    };

    // ── Save button: show rewarded ad, rescue on completion ──────
    saveBtn.on('pointerover', () => saveBtn.setFillStyle(0x55ee77));
    saveBtn.on('pointerout',  () => saveBtn.setFillStyle(0x44cc66));
    saveBtn.on('pointerdown', () => {
      // Stop countdown while ad is showing
      if (countdown) countdown.remove();

      showRewarded(
        // onEarned — user watched the full ad
        () => {
          if (this.rescueContainer) {
            this.rescueContainer.destroy(true);
            this.rescueContainer = null;
          }
          const gameScene = this.scene.get('GameScene');
          if (gameScene) gameScene.rescueGame();
        },
        // onFailed — ad failed to load or user dismissed early
        () => {
          // Restart countdown from 3 so they still have a moment to retry
          secondsLeft = 3;
          timerTxt.setText('3');
          countdown = this.time.addEvent({
            delay: 1000, loop: true,
            callback: () => {
              secondsLeft--;
              if (secondsLeft > 0) {
                timerTxt.setText(`${secondsLeft}`);
              } else {
                dismiss();
              }
            },
          });
        }
      );
    });

    giveUpTxt.on('pointerover',  () => giveUpTxt.setColor('#aaaaaa'));
    giveUpTxt.on('pointerout',   () => giveUpTxt.setColor('#888888'));
    giveUpTxt.on('pointerdown',  () => dismiss());

    // ── Countdown 5→0 ─────────────────────────────────────────────
    let secondsLeft = 5;
    let countdown = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        secondsLeft--;
        if (secondsLeft > 0) {
          timerTxt.setText(`${secondsLeft}`);
        } else {
          dismiss();
        }
      },
    });
  }

  /* ──────────────────── INTERSTITIAL AD ────────────────────────── */
  showInterstitialSim(onComplete) {
    // Delegates to AdManager — shows a real interstitial on device,
    // simulates a 1.5s delay in the browser.
    showInterstitial(onComplete);
  }

  showPaused() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    this.pauseContainer = this.add.container(0, 0).setDepth(20);

    const cardW = Math.min(320 * sd, W * 0.85);

    const gap       = 50 * sd;
    const titleY    = H / 2 - gap * 2.2;
    const subtitleY = titleY   + gap;
    const resumeY   = subtitleY + gap;
    const homeY     = resumeY  + gap;
    const musicY    = homeY    + gap * 0.78;
    const sfxY      = musicY   + gap * 0.72;

    const cardH       = sfxY - titleY + gap * 0.9;
    const cardCentreY = (titleY + sfxY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.7);
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    const title = this.add.text(W / 2, titleY, '⏸  PAUSED', {
      fontSize: `${26 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#ffcc00',
    }).setOrigin(0.5);

    const subtitle = this.add.text(W / 2, subtitleY, 'Your score will be lost if you quit here', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif', color: '#aaaaaa',
      wordWrap: { width: cardW * 0.85 }, align: 'center',
    }).setOrigin(0.5);

    const resumeBtnW = Math.min(240 * sd, W * 0.6);
    const resumeBtnH = 60 * sd;
    const resumeBtn  = this.add.rectangle(W / 2, resumeY, resumeBtnW, resumeBtnH, 0x90dd5c)
      .setInteractive({ useHandCursor: true });
    const resumeText = this.add.text(W / 2, resumeY, '▶  Resume', {
      fontSize: `${24 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif', color: '#1a1a2e',
    }).setOrigin(0.5);

    resumeBtn.on('pointerover', () => resumeBtn.setFillStyle(0xaae87a));
    resumeBtn.on('pointerout',  () => resumeBtn.setFillStyle(0x90dd5c));
    resumeBtn.on('pointerdown', () => {
      const gameScene = this.scene.get('GameScene');
      if (gameScene) gameScene.resumeGame();
    });

    const homeBtnW = Math.min(200 * sd, W * 0.5);
    const homeBtnH = 50 * sd;
    const homeBtn  = this.add.rectangle(W / 2, homeY, homeBtnW, homeBtnH, 0x4488ff)
      .setInteractive({ useHandCursor: true });
    const homeBtnText = this.add.text(W / 2, homeY, '🏠  Home', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    homeBtn.on('pointerover', () => homeBtn.setFillStyle(0x66aaff));
    homeBtn.on('pointerout',  () => homeBtn.setFillStyle(0x4488ff));
    homeBtn.on('pointerdown', () => {
      this.scene.stop('GameScene');
      this.scene.stop('UIScene');
      this.scene.start('HomeScene');
    });

    // ── Music toggle button ───────────────────────────────────────
    const toggleBtnW = Math.min(190 * sd, W * 0.46);
    const toggleBtnH = 38 * sd;

    const musicBtn = this.add.rectangle(W / 2, musicY, toggleBtnW, toggleBtnH, 0x223355)
      .setInteractive({ useHandCursor: true });
    const musicBtnText = this.add.text(
      W / 2, musicY,
      audioManager.isMuted ? '🔇  Music Off' : '🔊  Music On',
      {
        fontSize: `${15 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: '#ccddff', stroke: '#000000', strokeThickness: 2 * dpr,
      }
    ).setOrigin(0.5);

    musicBtn.on('pointerover',  () => musicBtn.setFillStyle(0x3355aa));
    musicBtn.on('pointerout',   () => musicBtn.setFillStyle(0x223355));
    musicBtn.on('pointerdown',  () => {
      audioManager.setMuted(!audioManager.isMuted);
      musicBtnText.setText(audioManager.isMuted ? '🔇  Music Off' : '🔊  Music On');
      musicBtn.setFillStyle(0x223355);
    });

    // ── Sound Effects toggle button ───────────────────────────────
    const sfxBtn = this.add.rectangle(W / 2, sfxY, toggleBtnW, toggleBtnH, 0x223355)
      .setInteractive({ useHandCursor: true });
    const sfxBtnText = this.add.text(
      W / 2, sfxY,
      audioManager.isSfxMuted ? '🔇  Effects Off' : '🔊  Effects On',
      {
        fontSize: `${15 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: '#ccddff', stroke: '#000000', strokeThickness: 2 * dpr,
      }
    ).setOrigin(0.5);

    sfxBtn.on('pointerover',  () => sfxBtn.setFillStyle(0x3355aa));
    sfxBtn.on('pointerout',   () => sfxBtn.setFillStyle(0x223355));
    sfxBtn.on('pointerdown',  () => {
      audioManager.setSfxMuted(!audioManager.isSfxMuted);
      sfxBtnText.setText(audioManager.isSfxMuted ? '🔇  Effects Off' : '🔊  Effects On');
      sfxBtn.setFillStyle(0x223355);
    });

    this.pauseContainer.add([
      overlay, card, title, subtitle,
      resumeBtn, resumeText,
      homeBtn, homeBtnText,
      musicBtn, musicBtnText,
      sfxBtn, sfxBtnText,
    ]);
  }

  /* ──────────────────── ROTATE AD OFFER ────────────────────────── */
  showRotateAdOffer() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.rotateAdContainer) return;

    this.rotateAdContainer = this.add.container(0, 0).setDepth(25);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 52 * sd;

    const titleY  = H / 2 - gap * 1.5;
    const subY    = titleY + gap * 0.85;
    const watchBtnY = subY + gap * 1.2;
    const noThanksY = watchBtnY + gap * 0.85;

    const cardH       = noThanksY - titleY + gap;
    const cardCentreY = (titleY + noThanksY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
      .setInteractive();  // Block clicks from going through
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffaa44);

    const titleTxt = this.add.text(W / 2, titleY, '🔄  Out of Rotations!', {
      fontSize: `${22 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffaa44',
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY,
      'Watch a short ad to get\n12 more rotations!', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#cccccc', align: 'center',
    }).setOrigin(0.5);

    // Watch Ad button
    const watchBtnW = Math.min(240 * sd, W * 0.6);
    const watchBtnH = 62 * sd;
    const watchBtn  = this.add.rectangle(W / 2, watchBtnY, watchBtnW, watchBtnH, 0x44cc66)
      .setInteractive({ useHandCursor: true });
    const watchBtnText = this.add.text(W / 2, watchBtnY, '📺  Watch Ad', {
      fontSize: `${21 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    // No thanks text-button
    const noThanksTxt = this.add.text(W / 2, noThanksY, 'No Thanks', {
      fontSize: `${14 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#888888',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.rotateAdContainer.add([
      overlay, card, titleTxt, subTxt,
      watchBtn, watchBtnText, noThanksTxt,
    ]);

    // Watch Ad button action
    watchBtn.on('pointerover', () => watchBtn.setFillStyle(0x55ee77));
    watchBtn.on('pointerout',  () => watchBtn.setFillStyle(0x44cc66));
    watchBtn.on('pointerup', () => {
      showRewarded(
        // onEarned — user watched the full ad
        () => {
          if (this.rotateAdContainer) {
            this.rotateAdContainer.destroy(true);
            this.rotateAdContainer = null;
          }
          const gameScene = this.scene.get('GameScene');
          if (gameScene) gameScene.refillRotations();
        },
        // onFailed — ad failed to load or user dismissed early
        () => {
          if (this.rotateAdContainer) {
            this.rotateAdContainer.destroy(true);
            this.rotateAdContainer = null;
          }
        }
      );
    });

    noThanksTxt.on('pointerover',  () => noThanksTxt.setColor('#aaaaaa'));
    noThanksTxt.on('pointerout',   () => noThanksTxt.setColor('#888888'));
    noThanksTxt.on('pointerup',  () => {
      if (this.rotateAdContainer) {
        this.rotateAdContainer.destroy(true);
        this.rotateAdContainer = null;
      }
    });
  }

  /* ────────────────── PREVIEW NEXT ANIMAL OFFER ────────────────── */
  showPreviewNextAnimalOffer() {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    if (this.previewAnimalContainer) return;

    const gameScene = this.scene.get('GameScene');
    if (!gameScene) return;

    this.previewAnimalContainer = this.add.container(0, 0).setDepth(25);

    const cardW = Math.min(300 * sd, W * 0.82);
    const gap   = 52 * sd;

    const titleY  = H / 2 - gap * 1.5;
    const subY    = titleY + gap * 0.85;
    const watchBtnY = subY + gap * 1.2;
    const noThanksY = watchBtnY + gap * 0.85;

    const cardH       = noThanksY - titleY + gap;
    const cardCentreY = (titleY + noThanksY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
      .setInteractive();  // Block clicks from going through
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0x6644cc);

    const titleTxt = this.add.text(W / 2, titleY, '🔮  Sneak Peek', {
      fontSize: `${22 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#8866ee',
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY,
      'Get a sneak peek of the\nnext animal!', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#cccccc', align: 'center',
    }).setOrigin(0.5);

    // Watch Ad button
    const watchBtnW = Math.min(240 * sd, W * 0.6);
    const watchBtnH = 62 * sd;
    const watchBtn  = this.add.rectangle(W / 2, watchBtnY, watchBtnW, watchBtnH, 0x6644cc)
      .setInteractive({ useHandCursor: true });
    const watchBtnText = this.add.text(W / 2, watchBtnY, '📺  Watch Ad', {
      fontSize: `${21 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    // No thanks text-button
    const noThanksTxt = this.add.text(W / 2, noThanksY, 'No Thanks', {
      fontSize: `${14 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#888888',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    this.previewAnimalContainer.add([
      overlay, card, titleTxt, subTxt,
      watchBtn, watchBtnText, noThanksTxt,
    ]);

    // Watch Ad button action
    watchBtn.on('pointerover', () => watchBtn.setFillStyle(0x8866ee));
    watchBtn.on('pointerout',  () => watchBtn.setFillStyle(0x6644cc));
    watchBtn.on('pointerup', () => {
      showRewarded(
        // onEarned — user watched the full ad
        () => {
          if (this.previewAnimalContainer) {
            this.previewAnimalContainer.destroy(true);
            this.previewAnimalContainer = null;
          }
          const nextAnimal = gameScene.getNextAnimalName();
          this.showNextAnimalReveal(nextAnimal, sd, dpr);
          gameScene.nextAnimalRevealed = true;
          gameScene.events.emit('nextAnimalStatusUpdate', true);
        },
        // onFailed — ad failed to load or user dismissed early
        () => {
          if (this.previewAnimalContainer) {
            this.previewAnimalContainer.destroy(true);
            this.previewAnimalContainer = null;
          }
        }
      );
    });

    noThanksTxt.on('pointerover',  () => noThanksTxt.setColor('#aaaaaa'));
    noThanksTxt.on('pointerout',   () => noThanksTxt.setColor('#888888'));
    noThanksTxt.on('pointerup',  () => {
      if (this.previewAnimalContainer) {
        this.previewAnimalContainer.destroy(true);
        this.previewAnimalContainer = null;
      }
    });
  }

  /* ────────────────── NEXT ANIMAL REVEAL ──────────────────────── */
  showNextAnimalReveal(animalName, sd, dpr) {
    const W = this.scale.width;
    const H = this.scale.height;

    if (this.nextAnimalRevealContainer) return;

    this.nextAnimalRevealContainer = this.add.container(0, 0).setDepth(26);

    const cardW = Math.min(300 * sd, W * 0.80);
    const gap   = 50 * sd;

    const titleY  = H / 2 - gap * 2.5;
    const imgY    = titleY + gap * 1.3;
    const nameY   = imgY + gap * 1.1;
    const closeBtnY = nameY + gap * 1.0;

    const cardH       = closeBtnY - titleY + gap * 1.4;
    const cardCentreY = (titleY + closeBtnY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.65);
    const card    = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    const titleTxt = this.add.text(W / 2, titleY, '✨ Next Animal ✨', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00',
    }).setOrigin(0.5);

    // Animal image - fixed size, no scaling
    const animalImg = this.add.image(W / 2, imgY, animalName)
      .setDisplaySize(60 * sd, 60 * sd)
      .setAlpha(0);

    // Fade in animation (no scale to prevent overflow)
    this.tweens.add({
      targets: animalImg,
      alpha: 1,
      duration: 400,
      ease: 'Power2',
    });

    // Animal name
    const animalNameTxt = this.add.text(W / 2, nameY, animalName.toUpperCase(), {
      fontSize: `${18 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#aaffaa',
    }).setOrigin(0.5);

    // Close button
    const closeBtnW = Math.min(140 * sd, cardW * 0.5);
    const closeBtnH = 42 * sd;
    const closeBtn  = this.add.rectangle(W / 2, closeBtnY, closeBtnW, closeBtnH, 0x4488ff)
      .setInteractive({ useHandCursor: true });
    const closeBtnText = this.add.text(W / 2, closeBtnY, 'Got It!', {
      fontSize: `${16 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    this.nextAnimalRevealContainer.add([
      overlay, card, titleTxt, animalImg, animalNameTxt, closeBtn, closeBtnText,
    ]);

    closeBtn.on('pointerover', () => closeBtn.setFillStyle(0x66aaff));
    closeBtn.on('pointerout',  () => closeBtn.setFillStyle(0x4488ff));
    closeBtn.on('pointerup', () => {
      if (this.nextAnimalRevealContainer) {
        this.nextAnimalRevealContainer.destroy(true);
        this.nextAnimalRevealContainer = null;
      }
    });
  }
}
